import React, { useEffect, useState } from "react";
import { postAPI } from "../../utils/common";
import { BOOKING_REPORT } from "../../constants/api";
import { Layout, Card, Button } from "antd";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import "./ReportsPage.css";
import { getRoomCapicityLabel } from "../../constants/constent";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";

dayjs.extend(utc);
dayjs.extend(localizedFormat);
const BookingReport = () => {
  const { Content } = Layout;
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [data, setData] = useState(null);

  function getDefaultStartDate() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
  }

  function getDefaultEndDate() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);
  }

  const fetchReport = () => {
    postAPI(
      BOOKING_REPORT,
      { start_date: startDate, end_date: endDate },
      (res) => {
        if (res.status) setData(res.data);
      },
      (err) => console.log(err)
    );
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const exportCSV = () => {
    const headers = ["Guest Name", "Check-In", "Check-Out", "Guests", "Status", "Discount", "Amount"];
    const rows = data.bookings.map((b) => [
      b.customer_name || "",
      dayjs.utc(b.checkin_date).format("ll, LT"),
      dayjs.utc(b.checkout_date).format("ll, LT"),
      getRoomCapicityLabel(b.room_type),
      b.booking_status === "0"
        ? "Pending"
        : b.booking_status === "1"
        ? "Ongoing"
        : b.booking_status === "2"
        ? "Completed"
        : b.booking_status === "3"
        ? "Cancelled"
        : "Unknown",
      b.discount_amount > 0 ? `-₹${b.discount_amount}` : "-",
      b.total_with_gst || "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "booking-report.csv";
    link.click();
  };

  const exportPDF = async () => {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF();
    autoTable(doc, {
      head: [
        ["Guest Name", "Check-In", "Check-Out", "Guests", "Status", "Discount", "Amount"]
      ],
      body: data.bookings.map((b) => [
        b.user.name || "",
        dayjs.utc(b.checkin_date).format("ll, LT"),
        dayjs.utc(b.checkout_date).format("ll, LT"),
        getRoomCapicityLabel(b.room_type),
        b.booking_status === "0"
          ? "Pending"
          : b.booking_status === "1"
          ? "Ongoing"
          : b.booking_status === "2"
          ? "Completed"
          : b.booking_status === "3"
          ? "Cancelled"
          : "Unknown",
        b.discount_amount > 0 ? `-₹${b.discount_amount}` : "-",
        b.total_with_gst || ""
      ]),
    });
    doc.save("booking-report.pdf");
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Booking Report"
          extra={
            <>
              <div className="filters">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <Button
                  onClick={fetchReport}
                  type="primary"
                  style={{ marginLeft: 10 }}
                >
                  Filter
                </Button>
              </div>
            </>
          }
        >
          <div className="reports-container">
            {data ? (
              <>
                <div className="card-group">
                  <Card title="Total Bookings">{data.summary.total_bookings}</Card>
                  <Card title="Total Nights">{data.summary.total_nights}</Card>
                  <Card title="Total Guests">{data.summary.total_guests}</Card>
                  <Card title="Total Revenue">₹ {data.summary.total_revenue}</Card>
                  <Card title="Total Discount Given">₹ {data.summary.total_discount_given ?? 0}</Card>
                </div>

                <div className="section">
                  <h3>Booking Status Breakdown</h3>
                  <div className="card-group">
                    {Object.entries(data.summary.status_breakdown).map(
                      ([key, value]) => (
                        <Card key={key} title={key.toUpperCase()}>
                          {value}
                        </Card>
                      )
                    )}
                  </div>
                </div>

                <div className="section">
                  <h3>Bookings List</h3>
                  <div style={{ marginBottom: 10 }}>
                    <Button onClick={exportCSV}>Export CSV</Button>
                    <Button onClick={exportPDF} style={{ marginLeft: 10 }}>
                      Export PDF
                    </Button>
                  </div>
                  <table className="booking-table">
                    <thead>
                      <tr>
                        <th>Guest Name</th>
                        <th>Check-In</th>
                        <th>Check-Out</th>
                        <th>Guests</th>
                        <th>Status</th>
                        <th>Discount</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.bookings.map((b, i) => (
                        <tr key={i}>
                          <td>{b.customer_name || "-"}</td>
                          <td>{dayjs.utc(b.checkin_date).format("ll, LT")}</td>
                          <td>{dayjs.utc(b.checkout_date).format("ll, LT")}</td>
                          <td>{getRoomCapicityLabel(b.room_type)}</td>
                          <td>
                            {b.booking_status === "0" ? "Pending"
                              : b.booking_status === "1" ? "Ongoing"
                              : b.booking_status === "2" ? "Completed"
                              : b.booking_status === "3" ? "Cancelled"
                              : "Unknown"}
                          </td>
                          <td style={{ color: b.discount_amount > 0 ? "red" : "inherit" }}>
                            {b.discount_amount > 0
                              ? `- ₹${b.discount_amount} ${b.discount_type === "percent" ? `(${b.discount_value}%)` : "(Flat)"}`
                              : "-"}
                          </td>
                          <td>{b.total_with_gst || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p>Loading...</p>
            )}
          </div>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
};

export default BookingReport;
